import { useEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { DateTime } from 'luxon';
import { useI18nContext } from '@/i18n/i18n-react';
import type { SalesCampaignFilterItem } from '@/components/sales-filters';
import type { SalesHistoryFilterValues } from '@/lib/sales-history';

export type PaymentStatusOption = { value: string; label: string };

const SINCE_BEGINNING = 'since_beginning';
const epochIso = () => DateTime.fromMillis(0).toISO() ?? '';
const nowIso = () => DateTime.now().toISO() ?? '';

/**
 * Filter state of the sales history (Vendas / Analytics › Pedidos). Prop
 * names match `SalesFilters`, so the state can be spread into it.
 */
export interface SalesHistoryFilters {
  values: SalesHistoryFilterValues;
  textFilter: string;
  setTextFilter: (value: string) => void;
  fromDateFilter: string;
  setFromDateFilter: (value: string) => void;
  toDateFilter: string;
  setToDateFilter: (value: string) => void;
  predefinedDateRange: string | undefined;
  setPredefinedDateRange: (value: string | undefined) => void;
  paymentStatusFilter: PaymentStatusOption[];
  setPaymentStatusFilter: Dispatch<SetStateAction<PaymentStatusOption[]>>;
  campaignsFilter: SalesCampaignFilterItem[];
  setCampaignsFilter: Dispatch<SetStateAction<SalesCampaignFilterItem[]>>;
  clearFilters: () => void;
  /** Page of the desktop table; back to 1 when status, campaigns or period change. */
  page: number;
  setPage: (page: number) => void;
  hasActiveFilters: boolean;
}

export function useSalesHistoryFilters(): SalesHistoryFilters {
  const { LL } = useI18nContext();

  const situations = useMemo<PaymentStatusOption[]>(
    () => [
      { value: 'paid', label: LL.salesPage.filters.paid() },
      { value: 'unpaid', label: LL.salesPage.filters.unpaid() },
    ],
    [LL]
  );

  const [page, setPage] = useState(1);
  const [textFilter, setTextFilter] = useState('');
  const [fromDateFilter, setFromDateFilter] = useState<string>(epochIso);
  const [toDateFilter, setToDateFilter] = useState<string>(nowIso);
  const [campaignsFilter, setCampaignsFilter] = useState<
    SalesCampaignFilterItem[]
  >([]);
  const [predefinedDateRange, setPredefinedDateRange] = useState<
    string | undefined
  >(SINCE_BEGINNING);
  const [paymentStatusFilter, setPaymentStatusFilter] =
    useState<PaymentStatusOption[]>(situations);

  // Relabel the selected statuses when the locale changes.
  useEffect(() => {
    setPaymentStatusFilter((selected) =>
      situations.filter((s) => selected.some((a) => a.value === s.value))
    );
  }, [situations]);

  useEffect(() => {
    setPage(1);
  }, [fromDateFilter, toDateFilter, paymentStatusFilter, campaignsFilter]);

  const clearFilters = () => {
    setTextFilter('');
    setFromDateFilter(epochIso());
    setToDateFilter(nowIso());
    setCampaignsFilter([]);
    setPaymentStatusFilter(situations);
    setPredefinedDateRange(SINCE_BEGINNING);
  };

  const values = useMemo<SalesHistoryFilterValues>(
    () => ({
      fromDate: fromDateFilter,
      toDate: toDateFilter,
      textSearch: textFilter,
      paymentStatus: paymentStatusFilter.map((s) => s.value),
      campaignIds: campaignsFilter.map((c) => c.id),
    }),
    [
      fromDateFilter,
      toDateFilter,
      textFilter,
      paymentStatusFilter,
      campaignsFilter,
    ]
  );

  const hasActiveFilters =
    textFilter.trim() !== '' ||
    predefinedDateRange !== SINCE_BEGINNING ||
    paymentStatusFilter.length !== situations.length ||
    campaignsFilter.length > 0;

  return {
    values,
    textFilter,
    setTextFilter,
    fromDateFilter,
    setFromDateFilter,
    toDateFilter,
    setToDateFilter,
    predefinedDateRange,
    setPredefinedDateRange,
    paymentStatusFilter,
    setPaymentStatusFilter,
    campaignsFilter,
    setCampaignsFilter,
    clearFilters,
    page,
    setPage,
    hasActiveFilters,
  };
}

/**
 * Period presets of the sales history (moved from `SalesFilters`): the
 * desktop popover and the mobile chips ("Personalizado · Desde o início").
 */
export function useSalesPredefinedDateRanges() {
  const { LL } = useI18nContext();

  return useMemo(
    () => [
      {
        label: LL.salesChart.predefinedRanges.today(),
        value: 'today',
        from: DateTime.now().startOf('day').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.yesterday(),
        value: 'yesterday',
        from: DateTime.now().minus({ days: 1 }).startOf('day').toISO(),
        to: DateTime.now().minus({ days: 1 }).endOf('day').toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.weekToDate(),
        value: 'week_to_date',
        from: DateTime.now().startOf('week').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last7Days(),
        value: 'last_7_days',
        from: DateTime.now().minus({ days: 7 }).startOf('day').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.monthToDate(),
        value: 'month_to_date',
        from: DateTime.now().startOf('month').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last30Days(),
        value: 'last_30_days',
        from: DateTime.now().minus({ days: 30 }).startOf('day').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last3Months(),
        value: 'last_3_months',
        from: DateTime.now().minus({ months: 3 }).toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last6Months(),
        value: 'last_6_months',
        from: DateTime.now().minus({ months: 6 }).toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.yearToDate(),
        value: 'year_to_date',
        from: DateTime.now().startOf('year').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last12Months(),
        value: 'last_12_months',
        from: DateTime.now().minus({ months: 12 }).toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.sinceBeginning(),
        value: 'since_beginning',
        from: DateTime.fromMillis(0).toISO() ?? '',
        to: DateTime.now().toISO(),
      },
    ],
    [LL]
  );
}
